// Tab group (<tab-group>): the tabs pick which rail is on screen.
// State is declarative, the hidden attribute on the panel, so nothing can
// be left half open by an interrupted click. Follows the APG pattern:
// arrows move between tabs, only the selected one is in the tab order.

class TabGroup extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    if (this.tabs.length < 2) return;

    this.listeners = new AbortController();
    const { signal } = this.listeners;

    this.addEventListener('click', (event) => {
      const tab = event.target.closest('[role="tab"]');
      if (tab) this.select(this.tabs.indexOf(tab));
    }, { signal });

    // Hover picks the tab too where the group asks for it: data-hover holds
    // the media query that decides, checked on each move so a resize counts
    // and a finger never leaves a tab stuck on the last touch.
    if (this.dataset.hover) {
      this.tabs.forEach((tab) => {
        tab.addEventListener('pointerenter', (event) => {
          if (event.pointerType !== 'mouse' || !matchMedia(this.dataset.hover).matches) return;
          this.select(this.tabs.indexOf(tab));
        }, { signal });
      });
    }

    // A vertical list moves with up and down, as the APG pattern asks.
    const vertical = this.querySelector('[role="tablist"]')?.getAttribute('aria-orientation') === 'vertical';
    const next = vertical ? 'ArrowDown' : 'ArrowRight';
    const previous = vertical ? 'ArrowUp' : 'ArrowLeft';

    this.addEventListener('keydown', (event) => {
      const step = { [next]: 1, [previous]: -1, Home: -Infinity, End: Infinity }[event.key];
      if (step === undefined) return;
      const current = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
      // `target`, e não `next`: `next` é a tecla de avançar, declarada
      // acima. Um `let next` aqui dentro sombreava aquele no bloco
      // inteiro, então a linha do `step` lia a variável na zona morta e
      // o handler quebrava com ReferenceError a cada tecla — as setas
      // não andavam em aba nenhuma do tema.
      let target = step === -Infinity ? 0 : step === Infinity ? this.tabs.length - 1 : current + step;
      target = (target + this.tabs.length) % this.tabs.length;
      event.preventDefault();
      this.select(target);
      this.tabs[target].focus();
    }, { signal });
  }

  disconnectedCallback() {
    this.listeners?.abort();
  }

  select(index) {
    // Com data-animate-height o grupo leva o que vem DEPOIS dele junto,
    // em vez de teletransportar: mede a altura antes e depois da troca e
    // anima a diferença. É o caso do menu do celular, onde o segundo
    // nível fica empilhado e cada aba tem um tanto de itens — sem isto
    // as fotos de coleção saltam até 320px de uma vez.
    //
    // Nada a fazer quando as alturas são iguais, que é o desktop com o
    // painel de seis linhas fixas: ali o `de` e o `para` batem e a
    // animação nem começa, inclusive na troca por hover.
    const anima = this.hasAttribute('data-animate-height');
    const de = anima ? this.getBoundingClientRect().height : 0;

    this.tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      const panel = document.getElementById(tab.getAttribute('aria-controls'));
      if (panel) panel.hidden = !selected;
    });

    if (anima) this.animateHeight(de);

    // The "view all" link, when the tabs carry a URL, follows the tab.
    const link = this.querySelector('[data-view-all]');
    const url = this.tabs[index]?.dataset.url;
    if (link && url) link.href = url;
  }

  // A duração sai do token do tema, então movimento reduzido (1ms) chega
  // aqui sozinho, sem uma segunda checagem para manter em dia.
  animateHeight(de) {
    const para = this.getBoundingClientRect().height;
    if (Math.round(de) === Math.round(para)) return;

    const ms = parseFloat(getComputedStyle(this).getPropertyValue('--motion-base')) || 240;
    this.heightAnimation?.cancel();
    // Enquanto a caixa é menor que o conteúdo, o excedente tem de ficar
    // guardado: sem isto a lista maior vaza por cima das fotos durante a
    // troca.
    this.style.overflow = 'clip';
    this.heightAnimation = this.animate(
      [{ height: `${de}px` }, { height: `${para}px` }],
      { duration: ms, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' },
    );
    const limpa = () => { this.style.overflow = ''; };
    this.heightAnimation.addEventListener('finish', limpa);
    this.heightAnimation.addEventListener('cancel', limpa);
  }
}

if (!customElements.get('tab-group')) {
  customElements.define('tab-group', TabGroup);
}
